import { Layout, Title, Select, Grid, Skeleton } from 'components'
import { Col, Flex } from '@mantine/core'
import { useEffect, useState, useCallback, useMemo } from 'react'
import learningService from './service/index'
import { Curriculum, Cycle, Area, SummaryReportItem } from './service/types/interfaces'
import { MultiSelect } from '@mantine/core'
import { CurrentUserContextProvider } from 'context'
import ReportTable from './components/ReportTable'
import { useTranslation } from 'react-i18next'

export function GeneralReport() {
  const [curriculums, setCurriculums] = useState<Curriculum[]>([])
  const [selectedCurriculum, setSelectedCurriculum] = useState<string>('')
  const [cycles, setCycles] = useState<Cycle[]>([])
  const [selectedCycles, setSelectedCycles] = useState<string[]>([])
  const [areas, setAreas] = useState<Area[]>([])
  const [selectedAreas, setSelectedAreas] = useState<string[]>([])
  const [curriculumOptions, setCurriculumOptions] = useState<{ value: string; label: string }[]>([])
  const [summaryReport, setSummaryReport] = useState<SummaryReportItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const { t } = useTranslation()

  const fetchLearningExpectations = useCallback(async () => {
    try {
      setLoading(true)
      const response = await learningService.getLearningExpectations()
      setCurriculums(response)
      setCurriculumOptions(response.map(c => ({ value: c.curriculumId, label: c.curriculum })))
    } catch (error) {
      console.error('Failed to fetch learning expectations', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLearningExpectations()
  }, [fetchLearningExpectations])

  const adjustSelectedCyclesFromCurriculum = useCallback(
    (curriculumId: string) => {
      const curriculum = curriculums.find(c => c.curriculumId === curriculumId)
      if (curriculum) {
        setCycles(curriculum.cycles || [])
        setAreas(curriculum.areas || [])
      } else {
        setCycles([])
        setAreas([])
      }
    },
    [curriculums],
  )

  const handleSelectCurriculum = useCallback(
    (value: string) => {
      setSelectedCurriculum(value)
      adjustSelectedCyclesFromCurriculum(value)
      setSelectedCycles([])
      setSelectedAreas([])
    },
    [adjustSelectedCyclesFromCurriculum],
  )

  const handleSelectCycles = useCallback((value: string[] | null) => {
    setSelectedCycles(value || [])
    setSelectedAreas([])
  }, [])

  const handleSelectAreas = useCallback((value: string[] | null) => {
    setSelectedAreas(value || [])
  }, [])

  const cycleOptions = useMemo(() => cycles.map(c => ({ value: c.id, label: c.name })), [cycles])
  const areaOptions = useMemo(() => areas.map(a => ({ value: a.id, label: a.name })), [areas])

  const fetchSummaryReport = useCallback(async () => {
    try {
      setLoading(true)
      if (selectedCurriculum && selectedCycles.length > 0) {
        const response = await learningService.getSummaryReport(selectedCurriculum, selectedCycles, selectedAreas)
        setSummaryReport(response)
      } else {
        setSummaryReport([])
      }
    } catch (error) {
      console.error('Failed to fetch summary report', error)
    } finally {
      setLoading(false)
    }
  }, [selectedCurriculum, selectedCycles, selectedAreas])

  useEffect(() => {
    fetchSummaryReport()
  }, [selectedCurriculum, selectedCycles, selectedAreas])

  return (
    <CurrentUserContextProvider>
      <Layout>
        <div>
          <Title mb='lg' order={2}>
            {t('generalReport')}
          </Title>
          <Grid>
            <Col span={12} md={4}>
              <Select
                label={t('generalReport_curriculum')}
                placeholder={t('generalReport_curriculum_select')}
                clearable
                data={curriculumOptions}
                value={selectedCurriculum}
                onChange={handleSelectCurriculum}
              />
            </Col>
            {selectedCurriculum && (
              <>
                <Col span={12} md={4}>
                  <MultiSelect
                    label={t('generalReport_curriculum_cycles')}
                    placeholder={t('generalReport_curriculum_select')}
                    data={cycleOptions}
                    clearable
                    value={selectedCycles}
                    onChange={handleSelectCycles}
                    multiple
                  />
                </Col>
                <Col span={12} md={4}>
                  <MultiSelect
                    label={t('generalReport_curriculum_areas')}
                    placeholder={t('generalReport_curriculum_select')}
                    clearable
                    data={areaOptions}
                    value={selectedAreas}
                    onChange={handleSelectAreas}
                    multiple
                    disabled={selectedCycles.length === 0}
                  />
                </Col>
              </>
            )}
          </Grid>
        </div>
        <Flex mt='md' />
        {loading ? <Skeleton height={200} /> : <ReportTable data={summaryReport} />}
      </Layout>
    </CurrentUserContextProvider>
  )
}
